import { useCallback, useEffect, useState } from "react";
import { Button, SettingsCard, useToast } from "@humansignal/ui";
import { useAPI } from "../../providers/ApiProvider";
import { cn } from "../../utils/bem";
import { Modal } from "../../components/Modal/Modal";
import "./RejectedWorkPanel.prefix.css";

const block = cn("rejected-work");

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      });
};

/**
 * "Your rejected work" — the annotator side of the fairness loop.
 * Lists the caller's rejected annotations on this project with the
 * reviewer's comment, and lets them request a re-review ONCE per
 * verdict with a required reason. Renders nothing when the caller has
 * no rejections here.
 */
export const RejectedWorkPanel = ({ projectId }) => {
  const api = useAPI();
  const toast = useToast();
  const [rows, setRows] = useState(null);
  const [disputing, setDisputing] = useState(null); // row being disputed, or null
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const res = await api.callApi("projectMyRejected", {
      params: { pk: projectId },
      suppressError: true,
    });
    if (res && !res.error) setRows(res.results ?? []);
  }, [api, projectId]);

  useEffect(() => {
    load();
  }, [load]);

  if (!rows || rows.length === 0) return null;

  const submit = async () => {
    if (!disputing || busy || reason.trim().length < 10) return;
    setBusy(true);
    const res = await api.callApi("annotationDispute", {
      params: { pk: disputing.id },
      body: { reason: reason.trim() },
      suppressError: true,
    });
    setBusy(false);
    if (!res || res.error) {
      toast.show({ message: res?.error ?? "Couldn't send the request, try again.", type: "error" });
      return;
    }
    toast.show({ message: "Re-review requested. The reviewer has been notified." });
    setDisputing(null);
    setReason("");
    load();
  };

  return (
    <SettingsCard
      title="Your rejected work"
      description="Rejections you can revise, or, if you believe a verdict is wrong, request a re-review with your reason."
    >
      <div className={block.elem("list").toClassName()}>
        {rows.map((r) => (
          <div key={r.id} className={block.elem("row").toClassName()}>
            <div className={block.elem("main").toClassName()}>
              <span className={block.elem("task").toClassName()}>Task #{r.task}</span>
              <span className={block.elem("date").toClassName()}>rejected {fmtDate(r.reviewed_at)}</span>
              {r.review_comment && <span className={block.elem("comment").toClassName()}>“{r.review_comment}”</span>}
            </div>
            {r.dispute_open ? (
              <span className={block.elem("status").mod({ open: true }).toClassName()}>Re-review requested</span>
            ) : r.dispute_upheld ? (
              <span className={block.elem("status").toClassName()}>Rejection upheld</span>
            ) : (
              <Button
                type="button"
                variant="neutral"
                look="outlined"
                size="smaller"
                onClick={() => {
                  setDisputing(r);
                  setReason("");
                }}
              >
                Request re-review
              </Button>
            )}
          </div>
        ))}
      </div>

      {disputing && (
        <Modal
          visible
          onHide={() => setDisputing(null)}
          title={`Request re-review. Task #${disputing.task}`}
          style={{ width: 480 }}
        >
          <div className={block.elem("modal-body").toClassName()}>
            {disputing.review_comment && (
              <p className={block.elem("modal-context").toClassName()}>
                Reviewer's comment: “{disputing.review_comment}”
              </p>
            )}
            <textarea
              className={block.elem("reason").toClassName()}
              rows={4}
              maxLength={2000}
              placeholder="Explain why you believe this verdict should be reconsidered…"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              autoFocus
            />
            <p className={block.elem("hint").toClassName()}>
              The reviewer sees your reason and takes another look. One request per verdict.
            </p>
            <div className={block.elem("actions").toClassName()}>
              <Button type="button" look="outlined" size="medium" onClick={() => setDisputing(null)} disabled={busy}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                size="medium"
                onClick={submit}
                disabled={busy || reason.trim().length < 10}
                waiting={busy}
              >
                {busy ? "Sending…" : "Send request"}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </SettingsCard>
  );
};
